import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { getPaymentEnvironment } from '@/lib/paddle';

const FREE_PLANT_LIMIT = 5;

interface Subscription {
  status: string;
  product_id: string;
  price_id: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  environment: string;
}

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const env = getPaymentEnvironment();

    const loadSubscription = async () => {
      const { data } = await supabase
        .from('subscriptions' as any)
        .select('status, product_id, price_id, current_period_end, cancel_at_period_end, environment')
        .eq('user_id', user.id)
        .eq('environment', env)
        .single();

      setSubscription(data as unknown as Subscription | null);
      setLoading(false);
    };

    loadSubscription();

    // Poll for subscription changes periodically (realtime not needed here —
    // subscription updates arrive via server-side webhooks)
    const interval = setInterval(loadSubscription, 30_000);
    return () => clearInterval(interval);
  }, [user]);

  const isActive = subscription
    ? ['active', 'trialing'].includes(subscription.status) &&
      (!subscription.current_period_end || new Date(subscription.current_period_end) > new Date())
    : false;

  const isPro = isActive;

  return {
    subscription,
    loading,
    isActive,
    isPro,
    freePlantLimit: FREE_PLANT_LIMIT,
    canAddPlant: (currentCount: number) => isPro || currentCount < FREE_PLANT_LIMIT,
  };
}
